import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import { formErrors, Problem, ValidationProblem } from "../api/backendApi";
import {
  EngineViewModel,
  ModelInfoModel,
  ModelInfoViewModel,
  modelInfoApi,
} from "../api/modelInfoApi";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import { MakeModel, makeAPI } from "../api/makeAPI";
import ModelInfoInputField from "../components/inputfields/ModelInfoInputField";
import ModelInfoInputFieldArray from "../components/inputfields/ModelInfoInputFieldArray";
import ModelInfoEngineEditButton from "../components/buttons/ModelInfoEngineEditButton";
import MonthYearPicker from "../components/pickers/MonthYearPicker";
import ModelInfoContextMenu, {
  AvailableOptions,
  ContextMenuOption,
} from "../components/contextmenus/ModelInfoContextMenu";
import React from "react";
import { useBlocker } from "react-router-dom";
import NavigationBlockerModal from "../components/modals/NavigationBlockerModal";
import { NumberPlateModal } from "../modelinfos/NumberPlateModal";
import { VinModal } from "../modelinfos/VinModal";
import { QrCodeModal } from "../modelinfos/QrCodeModal";
import HsnTsnModal from "../modelinfos/hsntsn/HsnTsnModal";
import { XLg } from "react-bootstrap-icons";
import ModelInfoArrayEditModal from "../components/modals/ModelInfoArrayEditModal";
import ImportModelInfoModal from "./ImportModelInfoModal";
import { bodyStyleApi, BodyStyleModel } from "../api/bodyStyleApi";
import { bodyStyleInfoApi, BodyStyleInfoModel } from "../api/bodyStyleInfoApi";
import ModelInfoSelectObject from "../components/inputfields/ModelInfoSelectObject";
import { fuelTypeApi, FuelTypeModel } from "../api/fuelTypeApi";
import { roofInfoTypeApi, RoofInfoTypeModel } from "../api/roofInfoTypeApi";
import { HybrideTypeModel, hybridTypeApi } from "../api/hybridTypeApi";
import { regionApi, RegionModel } from "../api/RegionApi";
import { VehicleFileViewModel, VehicleTypeModel } from "../api/vehicleFileAPI";
import { YearlyReviewDetails } from "./VehicleFileDetailsPage";

export interface Vehicle {
  index: number;
  initialValue: ModelInfoViewModel;
  currentValue: ModelInfoViewModel;
  isEngineModified: boolean;
  isMarkedForDeletion: boolean;
  isSelected: boolean;
}

interface ModelInfoProps {
  locationState?: YearlyReviewDetails;
  vehicleFile: VehicleFileViewModel;
  disabled: boolean;
  onModified: (isModified: boolean) => void;
  onLocationStateChange: (locationState: any) => void;
}

function ModelInfos({ locationState, vehicleFile, disabled, onModified, onLocationStateChange }: ModelInfoProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [errors, setErrors] = useState<formErrors>({});
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [alertMessage, setAlertMessage] = useState<string | undefined>(
    undefined,
  );
  const [makes, setMakes] = useState<MakeModel[]>([]);
  const [isModified, setIsModified] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [bodyStyles, setBodyStyles] = useState<BodyStyleModel[]>([]);
  const [bodyStyleInfos, setBodyStyleInfos] = useState<BodyStyleInfoModel[]>(
    [],
  );
  const [fuelTypes, setFuelTypes] = useState<FuelTypeModel[]>([]);
  const [roofInfoTypes, setRoofInfoTypes] = useState<RoofInfoTypeModel[]>([]);
  const [hybridTypes, setHybridTypes] = useState<HybrideTypeModel[]>([]);
  const [regions, setRegions] = useState<RegionModel[]>([]);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  // Context menu
  const [isContextMenuEnabled, setIsContextMenuEnabled] = useState(true);
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [contextMenuCell, setContextMenuCell] = useState<any>();
  const [isAllModelVariants, setIsAllModelVariants] = useState<boolean>(false);
  const [contextAllModelVariants, setContextAllModelVariants] =
    useState<boolean>(false);
  const [contextMenuKey, setContextMenuKey] = useState<string>("");
  const [cellKey, setCellKey] = useState<string>("");
  const [contextMenuOptions, setContextMenuOptions] = useState<
    ContextMenuOption[]
  >([]);
  const [contextMenuSelected, setContextMenuSelected] = useState<Vehicle>();
  const [contextMenuCopied, setContextMenuCopied] =
    useState<ModelInfoViewModel[]>();

  const [cellCopied, setCellCopied] = useState<any>();

  // Modals
  const [showNumberPlates, setShowNumberPlates] = useState(false);
  const [showVins, setShowVins] = useState(false);
  const [showQrCodes, setShowQrCodes] = useState(false);
  const [showHsnTsns, setShowHsnTsns] = useState(false);
  const [showDatNumbers, setShowDatNumbers] = useState(false);
  const [engineCell, setEngineCell] = useState<any>(undefined);
  const [engineKey, setEngineKey] = useState<string>("");

  // Sorting
  const [sortedColumn, setSortedColumn] = useState<string>("");
  const [selectedSortingColumn, setSelectedSortingColumn] =
    useState<string>("");

  // Constants
  const recId = "recId";
  const regionId = "regionId";
  const makeCode = "makeCode";
  const model = "model";
  const modelSeries = "modelSeries";
  const modelTypes = "modelTypes";
  const modelVariants = "modelVariants";
  const allModelVariants = "isAllModelVariants";
  const vehicleLengths = "vehicleLengths";
  const modelInfo = "modelInfo";
  const extraModelInfo = "extraModelInfo";
  const fuelTypeId = "fuelTypeId";
  const engine = "engine";
  const bodyStyleId = "bodyStyleId";
  const bodyStyleInfoId = "bodyStyleInfoId";
  const roofInfoTypeId = "roofInfoTypeId";
  const startDate = "startDate";
  const endDate = "endDate";
  const tradeNames = "tradeNames";
  const manufacturerMakeCode = "manufacturerMakeCode";
  const displayMakeCode = "displayMakeCode";

  const multiFields: string[] = [
    modelSeries,
    modelTypes,
    modelVariants,
    vehicleLengths,
    tradeNames,
  ];

  const loadModelInfo = useCallback(async () => {
    let response = await modelInfoApi.getModelInfo(vehicleFile.number);

    if (response.ok) {
      const compareNumber = (a: number, b: number) => {
        return a < b ? -1 : a > b ? 1 : 0;
      };
      const compareString = (a: string, b: string) => {
        return a.toLocaleLowerCase() < b.toLocaleLowerCase()
          ? -1
          : a.toLocaleLowerCase() > b.toLocaleLowerCase()
            ? 1
            : 0;
      };

      var vehiclesArray: Vehicle[] = response.value.map((modelInfo, index) => {
        modelInfo.vehicleLengths.sort(compareNumber);
        modelInfo.modelSeries.sort(compareString);
        modelInfo.modelTypes.sort(compareString);
        modelInfo.modelVariants.sort(compareString);
        modelInfo.tradeNames.sort(compareString);
        modelInfo.engine.engineCodes.sort(compareString);
        modelInfo.engine.drivetrainMaximumKws.sort(compareNumber);
        modelInfo.engine.drivetrainNominalKws.sort(compareNumber);
        modelInfo.engine.electric1MaximumKws.sort(compareNumber);
        modelInfo.engine.electric1NominalKws.sort(compareNumber);
        modelInfo.engine.electric2MaximumKws.sort(compareNumber);
        modelInfo.engine.electric2NominalKws.sort(compareNumber);
        modelInfo.engine.electric3MaximumKws.sort(compareNumber);
        modelInfo.engine.electric3NominalKws.sort(compareNumber);
        modelInfo.engine.electric4MaximumKws.sort(compareNumber);
        modelInfo.engine.electric4NominalKws.sort(compareNumber);
        modelInfo.engine.engineFrontAxle1MaximumKws.sort(compareNumber);
        modelInfo.engine.engineFrontAxle1NominalKws.sort(compareNumber);
        modelInfo.engine.engineFrontAxle2MaximumKws.sort(compareNumber);
        modelInfo.engine.engineFrontAxle2NominalKws.sort(compareNumber);
        modelInfo.engine.engineMaximumKws.sort(compareNumber);
        modelInfo.engine.engineNominalKws.sort(compareNumber);
        modelInfo.engine.engineRearAxle1MaximumKws.sort(compareNumber);
        modelInfo.engine.engineRearAxle1NominalKws.sort(compareNumber);
        modelInfo.engine.engineRearAxle2MaximumKws.sort(compareNumber);
        modelInfo.engine.engineRearAxle2NominalKws.sort(compareNumber);
        modelInfo.engine.mildHybridMaximumKws.sort(compareNumber);
        modelInfo.engine.mildHybridNominalKws.sort(compareNumber);
        return {
          index: index,
          initialValue: modelInfo,
          currentValue: modelInfo,
          isEngineModified: false,
          isMarkedForDeletion: false,
          isSelected: false,
        };
      });

      if (
        locationState !== undefined &&
        locationState !== null &&
        locationState.newYearEnd !== null &&
        locationState.reviewed === false
      ) {
        vehiclesArray = vehiclesArray.map((vehicle) => {
          if (
            vehicle.currentValue.monthEnd === null &&
            vehicle.currentValue.yearEnd === locationState.currentYear
          ) {
            vehicle.currentValue = {
              ...vehicle.currentValue,
              yearEnd: locationState.newYearEnd,
            };
          }
          return vehicle;
        });
      }

      setVehicles(vehiclesArray);

      if (response.value.length === 0) {
        setAlertMessage("Model info not found for this vehicle file number.");
      }
    } else {
      setError(response.problem);
    }
  }, [vehicleFile.number, locationState]);

  useEffect(() => {
    async function getModelInfo() {
      setLoading(true);
      setError(undefined);
      setErrors({});
      if (locationState?.reviewed === true) {
        setAlertMessage("Model info successfully saved.");
      } else {
        setAlertMessage(undefined);
      }
      setIsModified(false);

      await loadModelInfo();
      await loadBodyStyles();
      await loadBodyStyleInfos();
      await loadFuelTypes();
      await loadRoofInfoTypes();
      await loadHybridTypes();
      await loadRegions();
      loadMakes();
      setLoading(false);
    }
    getModelInfo();
  }, [vehicleFile.number, locationState, loadModelInfo]);

  useEffect(() => {
    const modified =
      !areVehiclesEqual(vehicles) ||
      !areEnginesEqual(vehicles) ||
      vehicles.some(
        (vehicle) =>
          vehicle.isMarkedForDeletion ||
          vehicle.currentValue.recId === undefined,
      );
    setIsModified(modified);
    onModified(modified);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  useEffect(() => {
    function loadHiddenColumns() {
      if (!showAllColumns) {
        const type = vehicleFile.type;

        if (type === VehicleTypeModel.Truck || type === VehicleTypeModel.Bus) {
          setHiddenColumns([
            roofInfoTypeId,
            tradeNames,
            manufacturerMakeCode,
            displayMakeCode,
          ]);
        } else if (
          type === VehicleTypeModel.Special ||
          type === VehicleTypeModel.Motorcycle ||
          type === VehicleTypeModel.Camper ||
          type === VehicleTypeModel.Agricultural ||
          type === VehicleTypeModel.Construction ||
          type === VehicleTypeModel.Vessel
        ) {
          setHiddenColumns([
            bodyStyleInfoId,
            roofInfoTypeId,
            tradeNames,
            manufacturerMakeCode,
            displayMakeCode,
          ]);
        } else if (
          type === VehicleTypeModel.Trailer ||
          type === VehicleTypeModel.EnergyStorageSystems
        ) {
          setHiddenColumns([
            modelSeries,
            modelTypes,
            modelVariants,
            vehicleLengths,
            bodyStyleInfoId,
            roofInfoTypeId,
            tradeNames,
            manufacturerMakeCode,
            displayMakeCode,
          ]);
        } else {
          // defaults, used for cars
          setHiddenColumns([tradeNames, manufacturerMakeCode, displayMakeCode]);
        }
      } else {
        setHiddenColumns([]);
      }
    }

    loadHiddenColumns();
  }, [vehicleFile.type, showAllColumns]);

  const isIncluded = (column: string): boolean => {
    return !hiddenColumns.includes(column);
  };

  const trimModelInfoValue = (value: string): string => {
    if (value === undefined) return "";
    return value.replace(/\s+/g, " ").trim();
  };

  function trimAllModelInfoModels(
    modelInfoModels: ModelInfoModel[],
  ): ModelInfoModel[] {
    return modelInfoModels.map(trimModelInfoModel);
  }

  function trimModelInfoModel(modelInfo: ModelInfoModel): ModelInfoModel {
    const trimValue = (value: any) => {
      if (typeof value === "string") return trimModelInfoValue(value);
      if (Array.isArray(value))
        return value.map((v) =>
          typeof v === "string" ? trimModelInfoValue(v) : v,
        );
      return value;
    };

    return {
      ...modelInfo,
      model: trimValue(modelInfo.model),
      modelSeries: trimValue(modelInfo.modelSeries),
      modelTypes: trimValue(modelInfo.modelTypes),
      modelVariants: trimValue(modelInfo.modelVariants),
      modelInfo: trimValue(modelInfo.modelInfo),
      extraModelInfo: trimValue(modelInfo.extraModelInfo),
      engine: {
        ...modelInfo.engine,
        engineCodes: trimValue(modelInfo.engine.engineCodes),
      },
      tradeNames: trimValue(modelInfo.tradeNames),
      datNumbers: trimValue(modelInfo.datNumbers),
    };
  }

  const saveModelInfo = async () => {
    setLoading(true);
    setError(undefined);
    setErrors({});
    setAlertMessage(undefined);

    const modelInfoModels: ModelInfoModel[] = vehicles
      .filter((vehicle) => !vehicle.isMarkedForDeletion)
      .map((vehicle) => vehicle.currentValue);

    const trimmedModelInfoModels = trimAllModelInfoModels(modelInfoModels);

    let response = await modelInfoApi.updateModelInfo(
      vehicleFile.number,
      trimmedModelInfoModels,
    );

    if (response.ok) {
      if (locationState !== null && locationState !== undefined && locationState?.reviewed === false) {
        locationState.reviewed = true;        
        onLocationStateChange(locationState);
      }

      await loadModelInfo();
      onModified(false);
      setIsModified(false);
    } else {
      setError(response.problem);
      let validation = response.problem as ValidationProblem;
      if (validation.validation) {
        setErrors(validation.validation);
      }
    }
    setLoading(false);
  };

  const createEmptyModelInfo = () => {
    const newEngineModel: EngineViewModel = {
      hybridTypeId: hybridTypes[0] ? hybridTypes[0].id : 0,
      cylinderCount: 0,
      engineSize: 0,
      engineCodes: [],
      drivetrainMaximumKws: [],
      drivetrainNominalKws: [],
      engineMaximumKws: [],
      engineNominalKws: [],
      engineFrontAxle1MaximumKws: [],
      engineFrontAxle1NominalKws: [],
      engineFrontAxle2MaximumKws: [],
      engineFrontAxle2NominalKws: [],
      engineRearAxle1MaximumKws: [],
      engineRearAxle1NominalKws: [],
      engineRearAxle2MaximumKws: [],
      engineRearAxle2NominalKws: [],
      mildHybridMaximumKws: [],
      mildHybridNominalKws: [],
      electric1MaximumKws: [],
      electric1NominalKws: [],
      electric2MaximumKws: [],
      electric2NominalKws: [],
      electric3MaximumKws: [],
      electric3NominalKws: [],
      electric4MaximumKws: [],
      electric4NominalKws: [],
    };

    const newModelInfoModel: ModelInfoViewModel = {
      recId: undefined,
      makeCode: vehicles[0]
        ? vehicles[0].currentValue.makeCode
        : vehicleFile.number.slice(0, 2),
      model: vehicles[0] ? vehicles[0].currentValue.model : "",
      modelSeries: [],
      modelTypes: [],
      modelVariants: [],
      regionId: regions[0]
        ? regions.find((region) => region.id === 2)?.id
        : regions[0],
      roofInfoTypeId: roofInfoTypes[0] ? roofInfoTypes[0].id : 1,
      vehicleLengths: [],
      modelInfo: "",
      extraModelInfo: "",
      fuelTypeId: 0,
      engine: newEngineModel,
      bodyStyleId:
        vehicles[0] && bodyStyles[0]
          ? vehicles[0].currentValue.bodyStyleId
          : bodyStyles[0].id,
      bodyStyleInfoId: 9,
      monthStart: 1,
      yearStart: new Date().getFullYear(),
      monthEnd: 12,
      yearEnd: new Date().getFullYear(),
      tradeNames: [],
      manufacturerMakeCode: "",
      displayMakeCode: "",
      datNumbers: [],
      hasHsnTsnValues: false,
      copiedFromRecId: undefined,
      isAllModelVariants: false,
    };

    return newModelInfoModel;
  };

  const addNewEntry = () => {
    const newModelInfoModel = createEmptyModelInfo();

    const newVehicle: Vehicle = {
      index: vehicles.length,
      initialValue: newModelInfoModel,
      currentValue: newModelInfoModel,
      isEngineModified: false,
      isMarkedForDeletion: false,
      isSelected: false,
    };

    setVehicles((prevVehicles) => [...prevVehicles, newVehicle]);
    setIsModified(true);
  };

  const loadMakes = async () => {
    const data = await makeAPI.getMakes();

    if (data.ok) {
      setError(undefined);
      setMakes(data.value);
    } else {
      setError(data.problem);
    }
  };

  const loadBodyStyles = async () => {
    const data = await bodyStyleApi.getBodyStyles();

    if (data.ok) {
      setError(undefined);
      setBodyStyles(data.value);
    } else {
      setError(data.problem);
    }
  };

  const loadBodyStyleInfos = async () => {
    const data = await bodyStyleInfoApi.getBodyStyleInfos();

    if (data.ok) {
      setError(undefined);
      setBodyStyleInfos(data.value);
    } else {
      setError(data.problem);
    }
  };

  const loadFuelTypes = async () => {
    const data = await fuelTypeApi.getFuelTypes();

    if (data.ok) {
      setError(undefined);
      setFuelTypes(data.value);
    } else {
      setError(data.problem);
    }
  };

  const loadRoofInfoTypes = async () => {
    const data = await roofInfoTypeApi.getRoofInfoTypes();

    if (data.ok) {
      setError(undefined);
      setRoofInfoTypes(data.value);
    } else {
      setError(data.problem);
    }
  };

  const loadHybridTypes = async () => {
    const data = await hybridTypeApi.getHybridTypes();

    if (data.ok) {
      setError(undefined);
      setHybridTypes(data.value);
    } else {
      setError(data.problem);
    }
  };

  const loadRegions = async () => {
    const data = await regionApi.GetRegions();

    if (data.ok) {
      setError(undefined);
      setRegions(data.value);
    } else {
      setError(data.problem);
    }
  };

  const updateVehicles = (
    index: number,
    column: string,
    value: any,
    isAllModelVariants?: boolean,
  ) => {
    setErrors({});
    setVehicles((v) => {
      const updatedVehicles = [...v];
      const updatedVehicle = { ...v[index] };

      updatedVehicle.currentValue = {
        ...updatedVehicle.currentValue,
        [column]: value,
      };

      if (isAllModelVariants !== undefined) {
        updatedVehicle.currentValue = {
          ...updatedVehicle.currentValue,
          [allModelVariants]: isAllModelVariants,
        };
      }

      updatedVehicles[index] = updatedVehicle;
      return updatedVehicles;
    });
  };

  const updateIsEngineModified = (index: number, isEngineModified: boolean) => {
    const updatedVehicles = [...vehicles];
    const updatedVehicle = { ...vehicles[index] };

    updatedVehicle.isEngineModified = isEngineModified;

    updatedVehicles[index] = updatedVehicle;

    setVehicles(updatedVehicles);
  };

  function areVehiclesEqual(vehicles: Vehicle[]): boolean {
    for (let i = 0; i < vehicles.length; i++) {
      if (!isModelInfoEqual(vehicles[i])) {
        return false;
      }
    }
    return true;
  }

  function areEnginesEqual(vehicles: Vehicle[]): boolean {
    for (let i = 0; i < vehicles.length; i++) {
      if (vehicles[i].isEngineModified) {
        return false;
      }
    }
    return true;
  }

  function isModelInfoEqual(vehicle: Vehicle): boolean {
    return (
      vehicle.initialValue.recId === vehicle.currentValue.recId &&
      vehicle.initialValue.makeCode === vehicle.currentValue.makeCode &&
      vehicle.initialValue.model === vehicle.currentValue.model &&
      vehicle.initialValue.modelSeries.join() ===
        vehicle.currentValue.modelSeries.join() &&
      vehicle.initialValue.modelTypes.join() ===
        vehicle.currentValue.modelTypes.join() &&
      vehicle.initialValue.modelVariants.join() ===
        vehicle.currentValue.modelVariants.join() &&
      vehicle.initialValue.regionId === vehicle.currentValue.regionId &&
      vehicle.initialValue.roofInfoTypeId ===
        vehicle.currentValue.roofInfoTypeId &&
      vehicle.initialValue.vehicleLengths.join() ===
        vehicle.currentValue.vehicleLengths.join() &&
      vehicle.initialValue.modelInfo === vehicle.currentValue.modelInfo &&
      vehicle.initialValue.extraModelInfo ===
        vehicle.currentValue.extraModelInfo &&
      vehicle.initialValue.fuelTypeId === vehicle.currentValue.fuelTypeId &&
      vehicle.isEngineModified === false &&
      vehicle.initialValue.bodyStyleId === vehicle.currentValue.bodyStyleId &&
      vehicle.initialValue.bodyStyleInfoId ===
        vehicle.currentValue.bodyStyleInfoId &&
      vehicle.initialValue.monthStart === vehicle.currentValue.monthStart &&
      vehicle.initialValue.yearStart === vehicle.currentValue.yearStart &&
      vehicle.initialValue.monthEnd === vehicle.currentValue.monthEnd &&
      vehicle.initialValue.yearEnd === vehicle.currentValue.yearEnd &&
      vehicle.initialValue.tradeNames.join() ===
        vehicle.currentValue.tradeNames.join() &&
      vehicle.initialValue.manufacturerMakeCode ===
        vehicle.currentValue.manufacturerMakeCode &&
      vehicle.initialValue.displayMakeCode ===
        vehicle.currentValue.displayMakeCode &&
      vehicle.initialValue.datNumbers.join() ===
        vehicle.currentValue.datNumbers.join() &&
      vehicle.initialValue.isAllModelVariants ===
        vehicle.currentValue.isAllModelVariants
    );
  }

  function isEngineEqual(
    initial: EngineViewModel,
    current: EngineViewModel,
  ): boolean {
    return (
      initial.hybridTypeId === current.hybridTypeId &&
      initial.cylinderCount === current.cylinderCount &&
      initial.engineSize === current.engineSize &&
      initial.engineCodes.join() === current.engineCodes.join() &&
      initial.drivetrainMaximumKws.join() ===
        current.drivetrainMaximumKws.join() &&
      initial.drivetrainNominalKws.join() ===
        current.drivetrainNominalKws.join() &&
      initial.engineMaximumKws.join() === current.engineMaximumKws.join() &&
      initial.engineNominalKws.join() === current.engineNominalKws.join() &&
      initial.engineFrontAxle1MaximumKws.join() ===
        current.engineFrontAxle1MaximumKws.join() &&
      initial.engineFrontAxle1NominalKws.join() ===
        current.engineFrontAxle1NominalKws.join() &&
      initial.engineFrontAxle2MaximumKws.join() ===
        current.engineFrontAxle2MaximumKws.join() &&
      initial.engineFrontAxle2NominalKws.join() ===
        current.engineFrontAxle2NominalKws.join() &&
      initial.engineRearAxle1MaximumKws.join() ===
        current.engineRearAxle1MaximumKws.join() &&
      initial.engineRearAxle1NominalKws.join() ===
        current.engineRearAxle1NominalKws.join() &&
      initial.engineRearAxle2MaximumKws.join() ===
        current.engineRearAxle2MaximumKws.join() &&
      initial.engineRearAxle2NominalKws.join() ===
        current.engineRearAxle2NominalKws.join() &&
      initial.mildHybridMaximumKws.join() ===
        current.mildHybridMaximumKws.join() &&
      initial.mildHybridNominalKws.join() ===
        current.mildHybridNominalKws.join() &&
      initial.electric1MaximumKws.join() ===
        current.electric1MaximumKws.join() &&
      initial.electric1NominalKws.join() ===
        current.electric1NominalKws.join() &&
      initial.electric2MaximumKws.join() ===
        current.electric2MaximumKws.join() &&
      initial.electric2NominalKws.join() ===
        current.electric2NominalKws.join() &&
      initial.electric3MaximumKws.join() ===
        current.electric3MaximumKws.join() &&
      initial.electric3NominalKws.join() ===
        current.electric3NominalKws.join() &&
      initial.electric4MaximumKws.join() ===
        current.electric4MaximumKws.join() &&
      initial.electric4NominalKws.join() === current.electric4NominalKws.join()
    );
  }

  const handleContextMenu = (
    event: React.MouseEvent<HTMLTableCellElement>,
    vehicle: Vehicle,
    cell: any = false,
    key: string = "",
    isAllModelVariant: boolean = false,
  ) => {
    const target = event.target as typeof event.target & Element;
    // don't trigger on inputs (they have their own context menu)
    if (target.nodeName === "INPUT") {
      setIsContextMenuVisible(false);
      return;
    }
    event.preventDefault();

    const position = {
      top: event.clientY,
      left: event.clientX,
    };

    setContextMenuPosition(position);
    setIsContextMenuVisible(true);
    setCellKey(key);
    setIsAllModelVariants(isAllModelVariant);

    if (cell !== false && (cell.length > 0 || cell !== undefined)) {
      setContextMenuCell(cell);
    }

    let selectedVehicles: Vehicle[];

    if (!vehicle.isSelected) {
      selectRow(vehicle, true, false);
      selectedVehicles = [vehicle];
    } else {
      selectedVehicles = vehicles.filter((v) => v.isSelected);
    }

    const selectedVehicleCount = selectedVehicles.length;
    const allMarkedForDeletion =
      selectedVehicles.filter((v) => v.isMarkedForDeletion).length ===
      selectedVehicles.length;
    const allNotMarkedForDeletion =
      selectedVehicles.filter((v) => !v.isMarkedForDeletion).length ===
      selectedVehicles.length;

    let options: ContextMenuOption[] = [];

    if (!disabled) {
      options.push(
        selectedVehicleCount === 1
          ? AvailableOptions.copyRow
          : AvailableOptions.copyRows,
      );

      if (
        selectedVehicleCount === 1 &&
        cell !== false &&
        cell !== "[None]" &&
        (cell.length > 0 ||
          (cell !== undefined && cell.length === undefined) ||
          (vehicle.currentValue.isAllModelVariants && key === modelVariants))
      ) {
        if (key === engine && vehicle.currentValue.modelInfo !== "?") {
          options.push(AvailableOptions.copyCell);
        } else if (key !== engine) {
          options.push(AvailableOptions.copyCell);
        }
      }

      if (multiFields.indexOf(key) >= 0 && selectedVehicleCount > 0) {
        if (selectedVehicleCount === 1)
          options.push(AvailableOptions.clearCell);
        else options.push(AvailableOptions.clearCells);
      }

      if (contextMenuCopied && contextMenuCopied.length > 0) {
        if (contextMenuCopied.length === selectedVehicleCount) {
          options.push(
            selectedVehicleCount === 1
              ? AvailableOptions.pasteRow
              : AvailableOptions.pasteRows,
          );
        }
      }

      if (
        typeof cell === typeof cellCopied &&
        cellCopied !== false &&
        (cellCopied.length > 0 || cellCopied !== undefined) &&
        selectedVehicleCount === 1 &&
        cell !== false
      ) {
        if (contextMenuKey === key && typeof cellCopied === "number") {
          options.push(AvailableOptions.pasteCell);
        } else if (typeof cellCopied !== "number") {
          if (
            (key === engine && vehicle.currentValue.modelInfo !== "?") ||
            key === startDate ||
            key === endDate ||
            key === vehicleLengths ||
            (key === modelVariants && contextAllModelVariants)
          ) {
            if (key === contextMenuKey) {
              options.push(AvailableOptions.pasteCell);
            }
          } else if (
            contextMenuKey !== engine &&
            contextMenuKey !== startDate &&
            contextMenuKey !== endDate &&
            key !== engine &&
            !contextAllModelVariants &&
            ((key === modelVariants && !isAllModelVariant) ||
              (key !== modelVariants && !contextAllModelVariants))
          ) {
            options.push(AvailableOptions.pasteCell);
          }
        }
      }

      if (
        typeof cell === typeof cellCopied &&
        cellCopied !== false &&
        (cellCopied.length > 0 || cellCopied !== undefined) &&
        selectedVehicleCount > 1 &&
        cell !== false
      ) {
        if (contextMenuKey === key && typeof cellCopied === "number") {
          options.push(AvailableOptions.pasteCells);
        } else if (typeof cellCopied !== "number") {
          if (
            (key === engine && vehicle.currentValue.modelInfo !== "?") ||
            key === startDate ||
            key === endDate ||
            key === vehicleLengths ||
            (key === modelVariants && contextAllModelVariants)
          ) {
            if (key === contextMenuKey) {
              options.push(AvailableOptions.pasteCells);
            }
          } else if (
            contextMenuKey !== engine &&
            contextMenuKey !== startDate &&
            contextMenuKey !== endDate &&
            key !== engine &&
            !contextAllModelVariants &&
            ((key === modelVariants && !isAllModelVariant) ||
              (key !== modelVariants && !contextAllModelVariants))
          ) {
            options.push(AvailableOptions.pasteCells);
          }
        }
      }

      if (allMarkedForDeletion) options.push(AvailableOptions.undeleteRow);
      else if (allNotMarkedForDeletion)
        options.push(AvailableOptions.deleteRow);
    }

    if (selectedVehicleCount === 1) {
      if (options.length > 0) {
        options.push(AvailableOptions.separator);
      }

      const isQuestionMark = vehicle.currentValue.modelInfo === "?";

      if (
        (isQuestionMark && vehicle.currentValue.regionId === 2) ||
        vehicle.currentValue.datNumbers.length > 0
      ) {
        options.push(AvailableOptions.viewDatNumbers);
      }

      options.push(AvailableOptions.viewQrCodes);

      if (!isQuestionMark) {
        options.push(AvailableOptions.viewNumberPlates);
        options.push(AvailableOptions.viewVins);
        options.push(AvailableOptions.viewHsnTsn);
      }
    }

    setContextMenuOptions(options);
    setContextMenuSelected(vehicle);
  };

  const pasteRow = () => {
    if (!contextMenuCopied || contextMenuCopied.length === 0) return;

    let selectedVehicles = vehicles.filter((v) => v.isSelected);

    if (contextMenuCopied.length !== selectedVehicles.length) {
      // they have to be the same length in order to be able to overwrite
      return;
    }

    let currentIndex = 0;

    const modifiedVehicles = vehicles.map((v) => {
      if (!v.isSelected) return v;

      let modifiedVehicle = { ...v };

      if (v.currentValue.modelInfo.trim() === "?") {
        modifiedVehicle.currentValue = {
          ...contextMenuCopied[currentIndex],
          recId: modifiedVehicle.currentValue.recId,
          modelVariants: v.currentValue.modelVariants,
          modelInfo: v.currentValue.modelInfo,
          engine: v.currentValue.engine,
        };
      } else {
        modifiedVehicle.currentValue = {
          ...contextMenuCopied[currentIndex],
          recId: modifiedVehicle.currentValue.recId,
        };
      }

      // if it's a new vehicle, keep track of the original recId if present
      if (!modifiedVehicle.currentValue.recId) {
        modifiedVehicle.currentValue.copiedFromRecId =
          contextMenuCopied[currentIndex].recId;
      }

      ++currentIndex;

      return modifiedVehicle;
    });

    setVehicles(modifiedVehicles);

    selectedVehicles = [...modifiedVehicles.filter((v) => v.isSelected)];

    const modified =
      !areVehiclesEqual(selectedVehicles) ||
      vehicles.some(
        (v) => v.isMarkedForDeletion || v.currentValue.recId === undefined,
      );
    setIsModified(modified);
    onModified(modified);
  };

  const clearCell = () => {
    const selectedVehicles: Vehicle[] = [];

    const modifiedVehicles = vehicles.map((v) => {
      if (!v.isSelected) return v;

      if (multiFields.indexOf(cellKey) >= 0) {
        const change = { ...v.currentValue, [cellKey]: [] };
        const modifiedVehicle = { ...v, currentValue: change };
        selectedVehicles.push(modifiedVehicle);

        return modifiedVehicle;
      } else {
        // not implemented
        return v;
      }
    });

    setVehicles(modifiedVehicles);

    const modified =
      !areVehiclesEqual(selectedVehicles) ||
      vehicles.some(
        (v) => v.isMarkedForDeletion || v.currentValue.recId === undefined,
      );
    setIsModified(modified);
    onModified(modified);
  };

  const pasteCell = () => {
    if (
      cellCopied === false ||
      (cellCopied.length === 0 && !contextAllModelVariants)
    )
      return;

    let modifiedVehicles = { ...vehicles };
    let change = {};

    if (contextMenuKey === startDate) {
      change = {
        monthStart: cellCopied.month,
        yearStart: cellCopied.year,
      };
    } else if (contextMenuKey === endDate) {
      change = {
        monthEnd: cellCopied.month,
        yearEnd: cellCopied.year,
      };
    } else {
      let copy = cellCopied;
      if (
        typeof cellCopied === "object" &&
        cellKey !== vehicleLengths &&
        cellKey !== engine
      ) {
        copy = cellCopied.map(String);
      }

      if (cellKey === modelVariants && contextAllModelVariants) {
        change = { [allModelVariants]: contextAllModelVariants };
      } else {
        change = { [cellKey]: copy };
      }
    }

    modifiedVehicles = vehicles.map((v) => {
      if (!v.isSelected) return v;
      let modifiedVehicle = { ...v };
      modifiedVehicle.currentValue = {
        ...v.currentValue,
        ...change,
      };

      return modifiedVehicle;
    });

    setVehicles(modifiedVehicles);

    if (contextMenuKey !== engine) {
      const modified =
        !areVehiclesEqual(modifiedVehicles) ||
        vehicles.some(
          (v) => v.isMarkedForDeletion || v.currentValue.recId === undefined,
        );

      setIsModified(modified);
      onModified(modified);
    }
  };

  const setDeletionMark = () => {
    const modifiedVehicles = vehicles.map((v) =>
      v.isSelected ? { ...v, isMarkedForDeletion: !v.isMarkedForDeletion } : v,
    );

    const modified =
      !areVehiclesEqual(vehicles) ||
      modifiedVehicles.some(
        (v) => v.isMarkedForDeletion || v.currentValue.recId === undefined,
      );
    setIsModified(modified);
    onModified(modified);
    setVehicles(modifiedVehicles);
  };

  const beforeUnloadHandler = (event: Event) => {
    event.preventDefault();
  };

  useEffect(() => {
    const handleBeforeUnload = (event: Event) => {
      if (isModified) {
        beforeUnloadHandler(event);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isModified]);

  let blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isModified && currentLocation.pathname !== nextLocation.pathname,
  );

  const selectRow = (
    vehicle: Vehicle,
    selected: boolean,
    keepValue: boolean,
  ) => {
    const modifiedVehicles = vehicles.map((v) => {
      let newSelected;

      if (v === vehicle) {
        newSelected = selected;
      } else if (keepValue) {
        newSelected = v.isSelected;
      } else {
        newSelected = false;
      }
      return { ...v, ...{ isSelected: newSelected } };
    });

    setVehicles(modifiedVehicles);
  };

  const [showImportModelInfo, setShowImportModelInfo] = useState(false);

  const handleImportModelInfoClick = () => {
    setShowImportModelInfo(true);
  };

  const handleModelInfoImport = async (importedVehicles: ModelInfoModel[]) => {
    if (importedVehicles.length > 0) {
      const newVehicles = importedVehicles.map((v, index) => ({
        index: vehicles.length + index,
        initialValue: createEmptyModelInfo(),
        currentValue: v,
        isEngineModified: true,
        isMarkedForDeletion: false,
        isSelected: false,
      }));

      setVehicles((current) => [...current, ...newVehicles]);
      setIsModified(true);
    }
    setShowImportModelInfo(false);
  };

  const validateArray = (
    vehicle: Vehicle,
    index: number,
    column: string,
  ): boolean => {
    var error = false;
    var array = vehicle.currentValue[column];

    for (let i = 0; i < array.length; i++) {
      column = column[0].toUpperCase() + column.substring(1);
      error = errors[`ModelInfos[${index}].${column}[${i}]`] ? true : false;
      if (error === true) {
        break;
      }
    }

    return error;
  };

  function sortVehicles(
    vehicles: Vehicle[],
    columnKey: keyof Vehicle["initialValue"] | keyof Vehicle["currentValue"],
  ): Vehicle[] {
    var isAlreadySorted = sortedColumn === columnKey;
    setSortedColumn(columnKey as string);

    return vehicles.sort((a, b) => {
      let aValue, bValue;

      if (columnKey === startDate) {
        aValue = getComparableDate(
          a.currentValue.monthStart,
          a.currentValue.yearStart,
        );
        bValue = getComparableDate(
          b.currentValue.monthStart,
          b.currentValue.yearStart,
        );
      } else if (columnKey === endDate) {
        aValue = getComparableDate(
          a.currentValue.monthEnd,
          a.currentValue.yearEnd,
        );
        bValue = getComparableDate(
          b.currentValue.monthEnd,
          b.currentValue.yearEnd,
        );
      } else {
        aValue = a.initialValue[columnKey] || a.currentValue[columnKey];
        bValue = b.initialValue[columnKey] || b.currentValue[columnKey];
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        if (isAlreadySorted) {
          setSortedColumn("");
          return bValue.localeCompare(aValue);
        } else {
          return aValue.localeCompare(bValue);
        }
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        if (isAlreadySorted) {
          setSortedColumn("");
          return bValue - aValue;
        } else {
          return aValue - bValue;
        }
      } else if (Array.isArray(aValue) && Array.isArray(bValue)) {
        if (isAlreadySorted) {
          setSortedColumn("");
          return bValue.join().localeCompare(aValue.join());
        } else {
          return aValue.join().localeCompare(bValue.join());
        }
      } else {
        return 0;
      }
    });
  }

  const getComparableDate = (month: number, year: number): number => {
    return year * 100 + month; // Example: 2023 * 100 + 5 = 202305
  };

  return (
    <>
      <div>
        {error && (
          <div
            style={{
              maxHeight: "150px",
              overflowY: "scroll",
              marginBottom: "5px",
            }}
          >
            <ErrorDisplay error={error} />
          </div>
        )}
        <LoadingDisplay loading={loading} />
        {alertMessage !== undefined && (
          <Alert variant="warning">{alertMessage}</Alert>
        )}
      </div>
      {!loading && (
        <>
          <div className="mb-3">
            {vehicles.length !== 0 && !disabled && (
              <Button
                className="me-3"
                onClick={saveModelInfo}
                disabled={!isModified}
                variant={isModified ? "warning" : "secondary"}
              >
                Save model info
              </Button>
            )}
            {!disabled && (
              <Button variant="success" onClick={addNewEntry}>
                Add new entry
              </Button>
            )}
            <Button
              className="ms-1"
              onClick={() => setShowAllColumns(!showAllColumns)}
            >
              {showAllColumns ? "Hide columns" : "Show all columns"}
            </Button>
            {!disabled && (
              <Button
                className="ms-3"
                variant="info"
                onClick={() => handleImportModelInfoClick()}
              >
                Copy model info
              </Button>
            )}
          </div>
          <div
            className="fixed-table-header"
            style={{ height: "auto", fontSize: "14px" }}
          >
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th
                    onClick={(e) => {
                      var sortedVehicles = sortVehicles([...vehicles], recId);
                      setVehicles(sortedVehicles);
                      setSelectedSortingColumn(recId);
                    }}
                    className={
                      selectedSortingColumn === recId
                        ? "selected-sorting-column"
                        : "column-header"
                    }
                  >
                    RecId
                  </th>
                  <th
                    onClick={(e) => {
                      var sortedVehicles = sortVehicles(
                        [...vehicles],
                        regionId,
                      );
                      setVehicles(sortedVehicles);
                      setSelectedSortingColumn(regionId);
                    }}
                    className={
                      selectedSortingColumn === regionId
                        ? "selected-sorting-column"
                        : "column-header"
                    }
                  >
                    Region
                  </th>
                  {showAllColumns && (
                    <th
                      onClick={(e) => {
                        var sortedVehicles = sortVehicles(
                          [...vehicles],
                          makeCode,
                        );
                        setVehicles(sortedVehicles);
                        setSelectedSortingColumn(makeCode);
                      }}
                      className={
                        selectedSortingColumn === makeCode
                          ? "selected-sorting-column"
                          : "column-header"
                      }
                    >
                      Make
                    </th>
                  )}
                  <th
                    onClick={(e) => {
                      var sortedVehicles = sortVehicles([...vehicles], model);
                      setVehicles(sortedVehicles);
                      setSelectedSortingColumn(model);
                    }}
                    className={
                      selectedSortingColumn === model
                        ? "selected-sorting-column"
                        : "column-header"
                    }
                  >
                    Model
                  </th>
                  {isIncluded(modelSeries) && (
                    <th
                      onClick={(e) => {
                        var sortedVehicles = sortVehicles(
                          [...vehicles],
                          modelSeries,
                        );
                        setVehicles(sortedVehicles);
                        setSelectedSortingColumn(modelSeries);
                      }}
                      className={
                        selectedSortingColumn === modelSeries
                          ? "selected-sorting-column text-nowrap"
                          : "text-nowrap column-header"
                      }
                    >
                      Model Series
                    </th>
                  )}
                  {isIncluded(modelTypes) && (
                    <th
                      onClick={(e) => {
                        var sortedVehicles = sortVehicles(
                          [...vehicles],
                          modelTypes,
                        );
                        setVehicles(sortedVehicles);
                        setSelectedSortingColumn(modelTypes);
                      }}
                      className={
                        selectedSortingColumn === modelTypes
                          ? "selected-sorting-column text-nowrap"
                          : "text-nowrap column-header"
                      }
                    >
                      Model Types
                    </th>
                  )}
                  {isIncluded(modelVariants) && (
                    <th
                      onClick={(e) => {
                        var sortedVehicles = sortVehicles(
                          [...vehicles],
                          modelVariants,
                        );
                        setVehicles(sortedVehicles);
                        setSelectedSortingColumn(modelVariants);
                      }}
                      className={
                        selectedSortingColumn === modelVariants
                          ? "selected-sorting-column text-nowrap"
                          : "text-nowrap column-header"
                      }
                    >
                      Model Variants
                    </th>
                  )}
                  {isIncluded(vehicleLengths) && (
                    <th
                      onClick={(e) => {
                        var sortedVehicles = sortVehicles(
                          [...vehicles],
                          vehicleLengths,
                        );
                        setVehicles(sortedVehicles);
                        setSelectedSortingColumn(vehicleLengths);
                      }}
                      className={
                        selectedSortingColumn === vehicleLengths
                          ? "selected-sorting-column text-nowrap"
                          : "text-nowrap column-header"
                      }
                    >
                      Vehicle Lengths
                    </th>
                  )}
                  <th
                    onClick={(e) => {
                      var sortedVehicles = sortVehicles(
                        [...vehicles],
                        modelInfo,
                      );
                      setVehicles(sortedVehicles);
                      setSelectedSortingColumn(modelInfo);
                    }}
                    className={
                      selectedSortingColumn === modelInfo
                        ? "selected-sorting-column text-nowrap"
                        : "text-nowrap column-header"
                    }
                  >
                    Model Info
                  </th>
                  <th
                    onClick={(e) => {
                      var sortedVehicles = sortVehicles(
                        [...vehicles],
                        extraModelInfo,
                      );
                      setVehicles(sortedVehicles);
                      setSelectedSortingColumn(extraModelInfo);
                    }}
                    className={
                      selectedSortingColumn === extraModelInfo
                        ? "selected-sorting-column text-nowrap"
                        : "text-nowrap column-header"
                    }
                  >
                    Extra Model Info
                  </th>
                  <th
                    onClick={(e) => {
                      var sortedVehicles = sortVehicles(
                        [...vehicles],
                        fuelTypeId,
                      );
                      setVehicles(sortedVehicles);
                      setSelectedSortingColumn(fuelTypeId);
                    }}
                    className={
                      selectedSortingColumn === fuelTypeId
                        ? "selected-sorting-column text-nowrap"
                        : "text-nowrap column-header"
                    }
                  >
                    Fuel Type
                  </th>
                  <th>Engine</th>
                  <th
                    onClick={(e) => {
                      var sortedVehicles = sortVehicles(
                        [...vehicles],
                        bodyStyleId,
                      );
                      setVehicles(sortedVehicles);
                      setSelectedSortingColumn(bodyStyleId);
                    }}
                    className={
                      selectedSortingColumn === bodyStyleId
                        ? "selected-sorting-column text-nowrap"
                        : "text-nowrap column-header"
                    }
                  >
                    Body Style
                  </th>
                  {isIncluded(bodyStyleInfoId) && (
                    <th
                      onClick={(e) => {
                        var sortedVehicles = sortVehicles(
                          [...vehicles],
                          bodyStyleInfoId,
                        );
                        setVehicles(sortedVehicles);
                        setSelectedSortingColumn(bodyStyleInfoId);
                      }}
                      className={
                        selectedSortingColumn === bodyStyleInfoId
                          ? "selected-sorting-column text-nowrap"
                          : "text-nowrap column-header"
                      }
                    >
                      Body Style Info
                    </th>
                  )}
                  {isIncluded(roofInfoTypeId) && (
                    <th
                      onClick={(e) => {
                        var sortedVehicles = sortVehicles(
                          [...vehicles],
                          roofInfoTypeId,
                        );
                        setVehicles(sortedVehicles);
                        setSelectedSortingColumn(roofInfoTypeId);
                      }}
                      className={
                        selectedSortingColumn === roofInfoTypeId
                          ? "selected-sorting-column text-nowrap"
                          : "text-nowrap column-header"
                      }
                    >
                      Roof Info
                    </th>
                  )}
                  <th
                    onClick={(e) => {
                      var sortedVehicles = sortVehicles(
                        [...vehicles],
                        startDate,
                      );
                      setVehicles(sortedVehicles);
                      setSelectedSortingColumn(startDate);
                    }}
                    className={
                      selectedSortingColumn === startDate
                        ? "selected-sorting-column text-nowrap"
                        : "text-nowrap column-header"
                    }
                  >
                    Date Start
                  </th>
                  <th
                    onClick={(e) => {
                      var sortedVehicles = sortVehicles([...vehicles], endDate);
                      setVehicles(sortedVehicles);
                      setSelectedSortingColumn(endDate);
                    }}
                    className={
                      selectedSortingColumn === endDate
                        ? "selected-sorting-column text-nowrap"
                        : "text-nowrap column-header"
                    }
                  >
                    Date End
                  </th>
                  {isIncluded(tradeNames) && (
                    <th
                      onClick={(e) => {
                        var sortedVehicles = sortVehicles(
                          [...vehicles],
                          tradeNames,
                        );
                        setVehicles(sortedVehicles);
                        setSelectedSortingColumn(tradeNames);
                      }}
                      className={
                        selectedSortingColumn === tradeNames
                          ? "selected-sorting-column text-nowrap"
                          : "text-nowrap column-header"
                      }
                    >
                      Trade Names
                    </th>
                  )}
                  {isIncluded(manufacturerMakeCode) && (
                    <th
                      onClick={(e) => {
                        var sortedVehicles = sortVehicles(
                          [...vehicles],
                          manufacturerMakeCode,
                        );
                        setVehicles(sortedVehicles);
                        setSelectedSortingColumn(manufacturerMakeCode);
                      }}
                      className={
                        selectedSortingColumn === manufacturerMakeCode
                          ? "selected-sorting-column text-nowrap"
                          : "text-nowrap column-header"
                      }
                    >
                      Manufacturer Make
                    </th>
                  )}
                  {isIncluded(displayMakeCode) && (
                    <th
                      onClick={(e) => {
                        var sortedVehicles = sortVehicles(
                          [...vehicles],
                          displayMakeCode,
                        );
                        setVehicles(sortedVehicles);
                        setSelectedSortingColumn(displayMakeCode);
                      }}
                      className={
                        selectedSortingColumn === displayMakeCode
                          ? "selected-sorting-column text-nowrap"
                          : "text-nowrap column-header"
                      }
                    >
                      Display Make
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {vehicles.map((vehicle, index) => (
                  <tr
                    className={vehicle.isSelected ? "table-primary" : ""}
                    key={index}
                    onClick={(e) =>
                      e.currentTarget.contains(e.target as HTMLElement) &&
                      selectRow(vehicle, !vehicle.isSelected, e.ctrlKey)
                    }
                  >
                    <td
                      className="align-middle select-table-cell"
                      onClick={(e) => {
                        e.stopPropagation();
                        selectRow(vehicle, !vehicle.isSelected, true);
                      }}
                      onContextMenu={(e) => handleContextMenu(e, vehicle)}
                    >
                      <Form.Check
                        checked={vehicle.isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          selectRow(vehicle, e.target.checked, true);
                        }}
                      />
                    </td>
                    <td
                      className="align-middle text-nowrap"
                      style={
                        vehicle.currentValue.hasHsnTsnValues
                          ? { color: "blue", fontSize: "14px" }
                          : { fontSize: "14px" }
                      }
                      onContextMenu={(e) => handleContextMenu(e, vehicle)}
                    >
                      {vehicle.currentValue.recId}
                      {vehicle.isMarkedForDeletion && <XLg color="red"></XLg>}
                    </td>
                    <td
                      onContextMenu={(e) =>
                        handleContextMenu(
                          e,
                          vehicle,
                          vehicle.currentValue.regionId,
                          regionId,
                        )
                      }
                    >
                      <ModelInfoSelectObject
                        name={regionId}
                        attributes={regions}
                        attributeId="id"
                        attributeField="english"
                        value={
                          vehicle.currentValue
                            ? vehicle.currentValue.regionId
                            : "Unknown"
                        }
                        alternativeValue="Unknown"
                        initialValue={vehicle.initialValue.regionId}
                        currentValue={vehicle.currentValue.regionId}
                        isNumbersOnly={true}
                        isOptional={false}
                        disabled={disabled}
                        onChange={(e) => updateVehicles(index, regionId, e)}
                      />
                    </td>
                    {showAllColumns && (
                      <td
                        onContextMenu={(e) =>
                          handleContextMenu(
                            e,
                            vehicle,
                            vehicle.currentValue.makeCode,
                            makeCode,
                          )
                        }
                      >
                        <ModelInfoSelectObject
                          name={makeCode}
                          attributes={makes}
                          attributeId="code"
                          attributeField="name"
                          value={vehicle.currentValue.makeCode}
                          alternativeValue="[None]"
                          initialValue={vehicle.initialValue.makeCode}
                          currentValue={vehicle.currentValue.makeCode}
                          isNumbersOnly={false}
                          isOptional={false}
                          disabled={disabled}
                          onChange={(e) => updateVehicles(index, makeCode, e)}
                        />
                      </td>
                    )}
                    <td onContextMenu={(e) => handleContextMenu(e, vehicle)}>
                      <ModelInfoInputField
                        error={
                          errors[`ModelInfos[${index}].Model`] !== undefined
                        }
                        columnName={model}
                        initialValue={vehicle.initialValue.model}
                        currentValue={vehicle.currentValue.model}
                        onlyAllowNumbers={false}
                        isRequired={true}
                        disabled={disabled}
                        onChange={(e) => updateVehicles(index, model, e)}
                      />
                    </td>
                    {isIncluded(modelSeries) && (
                      <td
                        onContextMenu={(e) => {
                          if (isContextMenuEnabled) {
                            handleContextMenu(
                              e,
                              vehicle,
                              vehicle.currentValue.modelSeries,
                              modelSeries,
                            );
                          }
                        }}
                      >
                        <ModelInfoInputFieldArray
                          error={validateArray(vehicle, index, "modelSeries")}
                          columnName={modelSeries}
                          initialValues={vehicle.initialValue.modelSeries}
                          currentValues={vehicle.currentValue.modelSeries}
                          onlyAllowNumbers={false}
                          allowDecimals={false}
                          onShow={(show) => setIsContextMenuEnabled(!show)}
                          disabled={disabled}
                          onChangeStringArray={(e) =>
                            updateVehicles(index, modelSeries, e)
                          }
                        />
                      </td>
                    )}
                    {isIncluded(modelTypes) && (
                      <td
                        onContextMenu={(e) => {
                          if (isContextMenuEnabled) {
                            handleContextMenu(
                              e,
                              vehicle,
                              vehicle.currentValue.modelTypes,
                              modelTypes,
                            );
                          }
                        }}
                      >
                        <ModelInfoInputFieldArray
                          error={validateArray(vehicle, index, "modelTypes")}
                          columnName={modelTypes}
                          initialValues={vehicle.initialValue.modelTypes}
                          currentValues={vehicle.currentValue.modelTypes}
                          onlyAllowNumbers={false}
                          allowDecimals={false}
                          onShow={(show) => setIsContextMenuEnabled(!show)}
                          disabled={disabled}
                          onChangeStringArray={(e) =>
                            updateVehicles(index, modelTypes, e)
                          }
                        />
                      </td>
                    )}
                    {isIncluded(modelVariants) && (
                      <td
                        onContextMenu={(e) => {
                          if (isContextMenuEnabled) {
                            handleContextMenu(
                              e,
                              vehicle,
                              vehicle.currentValue.modelVariants,
                              modelVariants,
                              vehicle.currentValue.isAllModelVariants,
                            );
                          }
                        }}
                      >
                        <ModelInfoInputFieldArray
                          error={validateArray(vehicle, index, "modelVariants")}
                          columnName={modelVariants}
                          allModelVariantsInitial={
                            vehicle.initialValue.isAllModelVariants
                          }
                          allModelVariants={
                            vehicle.currentValue.isAllModelVariants
                          }
                          initialValues={vehicle.initialValue.modelVariants}
                          currentValues={vehicle.currentValue.modelVariants}
                          onlyAllowNumbers={false}
                          allowDecimals={false}
                          onShow={(show) => setIsContextMenuEnabled(!show)}
                          disabled={disabled}
                          onChangeStringArray={(
                            newArray,
                            isAllModelVariants,
                          ) => {
                            updateVehicles(
                              index,
                              modelVariants,
                              newArray,
                              isAllModelVariants,
                            );
                          }}
                        />
                      </td>
                    )}
                    {isIncluded(vehicleLengths) && (
                      <td
                        onContextMenu={(e) => {
                          if (isContextMenuEnabled) {
                            handleContextMenu(
                              e,
                              vehicle,
                              vehicle.currentValue.vehicleLengths,
                              vehicleLengths,
                            );
                          }
                        }}
                      >
                        <ModelInfoInputFieldArray
                          columnName={vehicleLengths}
                          initialValues={vehicle.initialValue.vehicleLengths}
                          currentValues={vehicle.currentValue.vehicleLengths}
                          onlyAllowNumbers={true}
                          allowDecimals={false}
                          onShow={(show) => setIsContextMenuEnabled(!show)}
                          disabled={disabled}
                          onChangeNumberArray={(e) =>
                            updateVehicles(index, vehicleLengths, e)
                          }
                        />
                      </td>
                    )}
                    <td onContextMenu={(e) => handleContextMenu(e, vehicle)}>
                      <ModelInfoInputField
                        error={
                          errors[`ModelInfos[${index}].ModelInfo`] !== undefined
                        }
                        columnName={modelInfo}
                        initialValue={vehicle.initialValue.modelInfo}
                        currentValue={vehicle.currentValue.modelInfo}
                        onlyAllowNumbers={false}
                        isRequired={false}
                        disabled={disabled}
                        onChange={(e) => updateVehicles(index, modelInfo, e)}
                      />
                    </td>
                    <td onContextMenu={(e) => handleContextMenu(e, vehicle)}>
                      <ModelInfoInputField
                        error={
                          errors[`ModelInfos[${index}].ExtraModelInfo`] !==
                          undefined
                        }
                        columnName={extraModelInfo}
                        initialValue={vehicle.initialValue.extraModelInfo}
                        currentValue={vehicle.currentValue.extraModelInfo}
                        onlyAllowNumbers={false}
                        isRequired={false}
                        disabled={disabled}
                        onChange={(e) =>
                          updateVehicles(index, extraModelInfo, e)
                        }
                      />
                    </td>
                    <td
                      onContextMenu={(e) =>
                        handleContextMenu(
                          e,
                          vehicle,
                          vehicle.currentValue.fuelTypeId,
                          fuelTypeId,
                        )
                      }
                    >
                      <ModelInfoSelectObject
                        name={fuelTypeId}
                        attributes={fuelTypes}
                        attributeId="id"
                        attributeField="type"
                        value={
                          vehicle.currentValue
                            ? vehicle.currentValue.fuelTypeId
                            : 1
                        }
                        alternativeValue="Unknown"
                        initialValue={vehicle.initialValue?.fuelTypeId}
                        currentValue={vehicle.currentValue?.fuelTypeId}
                        isNumbersOnly={true}
                        isOptional={false}
                        disabled={disabled}
                        onChange={(e) => updateVehicles(index, fuelTypeId, e)}
                      />
                    </td>
                    <td
                      style={{ textAlign: "center" }}
                      onContextMenu={(e) => {
                        if (isContextMenuEnabled) {
                          handleContextMenu(
                            e,
                            vehicle,
                            vehicle.currentValue.engine,
                            engine,
                          );
                        }
                      }}
                    >
                      {vehicle.currentValue.modelInfo !== "?" && (
                        <ModelInfoEngineEditButton
                          vehicle={vehicle}
                          hybridTypes={hybridTypes}
                          disabled={disabled}
                          onContextMenu={(cell, key) => {
                            setEngineCell(cell);
                            setEngineKey(key);
                          }}
                          engineCell={engineCell ? engineCell : undefined}
                          engineKey={engineKey !== "" ? engineKey : ""}
                          onShow={(show) => setIsContextMenuEnabled(!show)}
                          onChange={(engineViewModel, isEngineModified) => {
                            vehicle.isEngineModified = isEngineModified;
                            updateVehicles(index, engine, engineViewModel);
                          }}
                          updateIsEngineModified={(
                            initialEngine,
                            currentEngine,
                          ) => {
                            vehicle.isEngineModified = !isEngineEqual(
                              initialEngine,
                              currentEngine,
                            );
                            updateIsEngineModified(
                              index,
                              vehicle.isEngineModified,
                            );
                          }}
                        />
                      )}
                    </td>
                    <td
                      onContextMenu={(e) =>
                        handleContextMenu(
                          e,
                          vehicle,
                          vehicle.currentValue.bodyStyleId,
                          bodyStyleId,
                        )
                      }
                    >
                      <ModelInfoSelectObject
                        name={bodyStyleId}
                        attributes={bodyStyles}
                        attributeId="id"
                        attributeField="style"
                        value={
                          vehicle.currentValue
                            ? vehicle.currentValue.bodyStyleId
                            : "[None]"
                        }
                        alternativeValue="[None]"
                        initialValue={vehicle.initialValue?.bodyStyleId}
                        currentValue={vehicle.currentValue?.bodyStyleId}
                        isNumbersOnly={true}
                        isOptional={false}
                        disabled={disabled}
                        onChange={(e) => updateVehicles(index, bodyStyleId, e)}
                      />
                    </td>
                    {isIncluded(bodyStyleInfoId) && (
                      <td
                        onContextMenu={(e) =>
                          handleContextMenu(
                            e,
                            vehicle,
                            vehicle.currentValue.bodyStyleInfoId,
                            bodyStyleInfoId,
                          )
                        }
                      >
                        <ModelInfoSelectObject
                          name={bodyStyleInfoId}
                          attributes={bodyStyleInfos}
                          attributeId="id"
                          attributeField="english"
                          value={
                            vehicle.currentValue
                              ? vehicle.currentValue.bodyStyleInfoId
                              : 9
                          }
                          alternativeValue="Empty"
                          initialValue={vehicle.initialValue?.bodyStyleInfoId}
                          currentValue={vehicle.currentValue?.bodyStyleInfoId}
                          isNumbersOnly={true}
                          isOptional={false}
                          disabled={disabled}
                          onChange={(e) =>
                            updateVehicles(index, bodyStyleInfoId, e)
                          }
                        />
                      </td>
                    )}
                    {isIncluded(roofInfoTypeId) && (
                      <td
                        onContextMenu={(e) =>
                          handleContextMenu(
                            e,
                            vehicle,
                            vehicle.currentValue.roofInfoTypeId,
                            roofInfoTypeId,
                          )
                        }
                      >
                        <ModelInfoSelectObject
                          name={roofInfoTypeId}
                          attributes={roofInfoTypes}
                          attributeId="id"
                          attributeField="english"
                          value={
                            vehicle.currentValue
                              ? vehicle.currentValue.roofInfoTypeId
                              : 1
                          }
                          alternativeValue="Unknown"
                          initialValue={vehicle.initialValue?.roofInfoTypeId}
                          currentValue={vehicle.currentValue?.roofInfoTypeId}
                          isNumbersOnly={true}
                          isOptional={false}
                          disabled={disabled}
                          onChange={(e) =>
                            updateVehicles(index, roofInfoTypeId, e)
                          }
                        />
                      </td>
                    )}
                    <td
                      onContextMenu={(e) =>
                        handleContextMenu(
                          e,
                          vehicle,
                          {
                            month: vehicle.currentValue.monthStart,
                            year: vehicle.currentValue.yearStart,
                          },
                          startDate,
                        )
                      }
                    >
                      <MonthYearPicker
                        monthColumn="monthStart"
                        yearColumn="yearStart"
                        initialMonth={vehicle.initialValue.monthStart}
                        initialYear={vehicle.initialValue.yearStart}
                        currentMonth={vehicle.currentValue.monthStart}
                        currentYear={vehicle.currentValue.yearStart}
                        isEndDate={false}
                        disabled={disabled}
                        onChange={(value, column) => {
                          updateVehicles(index, column, value);
                        }}
                      />
                    </td>
                    <td
                      onContextMenu={(e) =>
                        handleContextMenu(
                          e,
                          vehicle,
                          {
                            month: vehicle.currentValue.monthEnd,
                            year: vehicle.currentValue.yearEnd,
                          },
                          endDate,
                        )
                      }
                    >
                      <MonthYearPicker
                        monthColumn="monthEnd"
                        yearColumn="yearEnd"
                        initialMonth={vehicle.initialValue.monthEnd}
                        initialYear={vehicle.initialValue.yearEnd}
                        currentMonth={vehicle.currentValue.monthEnd}
                        currentYear={vehicle.currentValue.yearEnd}
                        isEndDate={true}
                        disabled={disabled}
                        onChange={(value, column) => {
                          updateVehicles(index, column, value);
                        }}
                      />
                    </td>
                    {isIncluded(tradeNames) && (
                      <td
                        onContextMenu={(e) => {
                          if (isContextMenuEnabled) {
                            handleContextMenu(
                              e,
                              vehicle,
                              vehicle.currentValue.tradeNames,
                              tradeNames,
                            );
                          }
                        }}
                      >
                        <ModelInfoInputFieldArray
                          error={validateArray(vehicle, index, "tradeNames")}
                          columnName={tradeNames}
                          initialValues={vehicle.initialValue.tradeNames}
                          currentValues={vehicle.currentValue.tradeNames}
                          onlyAllowNumbers={false}
                          allowDecimals={false}
                          onShow={(show) => setIsContextMenuEnabled(!show)}
                          disabled={disabled}
                          onChangeStringArray={(e) =>
                            updateVehicles(index, tradeNames, e)
                          }
                        />
                      </td>
                    )}
                    {isIncluded(manufacturerMakeCode) && (
                      <td
                        onContextMenu={(e) =>
                          handleContextMenu(
                            e,
                            vehicle,
                            vehicle.currentValue.manufacturerMakeCode
                              ? vehicle.currentValue.manufacturerMakeCode
                              : "[None]",
                            manufacturerMakeCode,
                          )
                        }
                      >
                        <ModelInfoSelectObject
                          name={manufacturerMakeCode}
                          attributes={makes}
                          attributeId="code"
                          attributeField="name"
                          value={vehicle.currentValue.manufacturerMakeCode}
                          alternativeValue="[None]"
                          initialValue={
                            vehicle.initialValue.manufacturerMakeCode
                          }
                          currentValue={
                            vehicle.currentValue.manufacturerMakeCode
                          }
                          isNumbersOnly={false}
                          isOptional={true}
                          disabled={disabled}
                          onChange={(e) =>
                            updateVehicles(
                              index,
                              manufacturerMakeCode,
                              e === "[None]" ? null : e,
                            )
                          }
                        />
                      </td>
                    )}
                    {isIncluded(displayMakeCode) && (
                      <td
                        onContextMenu={(e) =>
                          handleContextMenu(
                            e,
                            vehicle,
                            vehicle.currentValue
                              ? vehicle.currentValue.displayMakeCode
                              : "[None]",
                            displayMakeCode,
                          )
                        }
                      >
                        <ModelInfoSelectObject
                          name={displayMakeCode}
                          attributes={makes}
                          attributeId="code"
                          attributeField="name"
                          value={vehicle.currentValue.displayMakeCode}
                          alternativeValue="[None]"
                          initialValue={vehicle.initialValue.displayMakeCode}
                          currentValue={vehicle.currentValue.displayMakeCode}
                          isNumbersOnly={false}
                          isOptional={true}
                          disabled={disabled}
                          onChange={(e) =>
                            updateVehicles(
                              index,
                              displayMakeCode,
                              e === "[None]" ? null : e,
                            )
                          }
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}

      {isContextMenuVisible && (
        <ModelInfoContextMenu
          onClose={() => setIsContextMenuVisible(false)}
          onCopy={() =>
            setContextMenuCopied(
              vehicles
                .filter((v) => v.isSelected)
                .map((v) => ({ ...v.currentValue })),
            )
          }
          onCopyCell={() => {
            setContextAllModelVariants(isAllModelVariants);
            setContextMenuKey(cellKey);
            setCellCopied(contextMenuCell);
          }}
          onClearCell={clearCell}
          onPasteCell={pasteCell}
          onPasteCells={pasteCell}
          onPaste={pasteRow}
          onAddNumberPlate={() => setShowNumberPlates(true)}
          onAddVin={() => setShowVins(true)}
          onAddQrCode={() => setShowQrCodes(true)}
          onAddHsnTsn={() => setShowHsnTsns(true)}
          onMarkForDeletion={setDeletionMark}
          onViewDatNumbers={() => setShowDatNumbers(true)}
          options={contextMenuOptions}
          style={{
            position: "absolute",
            top: contextMenuPosition.top,
            left: contextMenuPosition.left,
            zIndex: 1060,
          }}
        />
      )}

      {blocker.state === "blocked" ? (
        <NavigationBlockerModal
          onProceed={() => blocker.proceed?.()}
          onCancel={() => blocker.reset?.()}
        />
      ) : null}

      {showNumberPlates && (
        <NumberPlateModal
          recId={
            contextMenuSelected?.currentValue.recId
              ? contextMenuSelected.currentValue.recId
              : 1
          }
          onCancel={() => setShowNumberPlates(false)}
          modalText={`View Number Plates for ${contextMenuSelected?.currentValue.recId}`}
          disabled={disabled}
        ></NumberPlateModal>
      )}

      {showVins && (
        <VinModal
          recId={
            contextMenuSelected?.currentValue.recId
              ? contextMenuSelected.currentValue.recId
              : 1
          }
          onCancel={() => setShowVins(false)}
          modalText={`View VINs for ${contextMenuSelected?.currentValue.recId}`}
          disabled={disabled}
        ></VinModal>
      )}

      {showQrCodes && (
        <QrCodeModal
          recId={
            contextMenuSelected?.currentValue.recId
              ? contextMenuSelected.currentValue.recId
              : 1
          }
          onCancel={() => setShowQrCodes(false)}
          modalText={`View QR Codes for ${contextMenuSelected?.currentValue.recId}`}
          disabled={disabled}
        ></QrCodeModal>
      )}
      {showHsnTsns && (
        <HsnTsnModal
          recId={
            contextMenuSelected?.currentValue.recId
              ? contextMenuSelected.currentValue.recId
              : 1
          }
          onCancel={() => setShowHsnTsns(false)}
          onSave={(hasSelection) =>
            contextMenuSelected &&
            (contextMenuSelected.currentValue.hasHsnTsnValues = hasSelection)
          }
          modalText={`View Hsn/Tsn for ${contextMenuSelected?.currentValue.recId}`}
          disabled={disabled}
        ></HsnTsnModal>
      )}
      {showDatNumbers && (
        <ModelInfoArrayEditModal
          isSortable={true}
          columnName={"datNumbers"}
          currentValues={
            contextMenuSelected
              ? contextMenuSelected.currentValue.datNumbers
              : []
          }
          onlyAllowNumbers={false}
          allowDecimals={false}
          disabled={
            disabled ||
            contextMenuSelected?.currentValue.regionId !== 2 ||
            (contextMenuSelected?.currentValue.modelInfo.trim() !== "?" &&
              contextMenuSelected?.currentValue.datNumbers.length === 0)
          }
          onCancel={(e) => {
            updateVehicles(
              contextMenuSelected ? contextMenuSelected.index : 0,
              "datNumbers",
              e,
            );
            setShowDatNumbers(false);
          }}
        />
      )}
      {showImportModelInfo && (
        <ImportModelInfoModal
          vehicleFileNumber={vehicleFile.number}
          onClose={() => setShowImportModelInfo(false)}
          onImport={(mis) => handleModelInfoImport(mis)}
        />
      )}
    </>
  );
}

export default ModelInfos;
